import { LocalStorageKey } from '@/interfaces';
import { DOMAIN_CONFIGS, DomainConfig } from '../configs';

/**
 * 域名配置管理器
 * 集成域名检测、配置管理和缓存功能的单例类
 */
class DomainManager {
  private static instance: DomainManager;
  private cachedIsSinoMotive: boolean | null = null;
  private cachedDomainConfig: DomainConfig | null = null;
  private cachedStorageKeys: Record<string, string> | null = null;

  private constructor() {}

  /**
   * 获取单例实例
   */
  public static getInstance(): DomainManager {
    if (!DomainManager.instance) {
      DomainManager.instance = new DomainManager();
    }
    return DomainManager.instance;
  }

  /**
   * 检测是否是SinoMotive域名（带缓存）
   */
  public isSinoMotive(): boolean {
    return true;
    // if (this.cachedIsSinoMotive === null) {
    //   this.cachedIsSinoMotive = !this.detectSinoMotiveDomain();
    // }
    // return this.cachedIsSinoMotive;
  }

  /**
   * 获取当前域名配置（带缓存）
   */
  public getDomainConfig(): DomainConfig {
    if (this.cachedDomainConfig === null) {
      this.cachedDomainConfig = this.isSinoMotive()
        ? DOMAIN_CONFIGS.SINOMOTIVE
        : DOMAIN_CONFIGS.CASSCHOICE;
    }
    return this.cachedDomainConfig;
  }

  /**
   * 获取本地存储键配置（带缓存）
   */
  public getLocalStorageKeys(): Record<string, string> {
    if (this.cachedStorageKeys === null) {
      const config = this.getDomainConfig();
      const prefix = config.storagePrefix;

      this.cachedStorageKeys = {
        language: `${prefix}language`,
        country: `${prefix}country`,
        currency: `${prefix}currency`,
        userInfo: `${prefix}userInfo`,
        direction: `${prefix}direction`,
        account: `${prefix}account`,
      };
    }
    return this.cachedStorageKeys;
  }

  /**
   * 获取特定的本地存储键
   */
  public getStorageKey(key: LocalStorageKey): string {
    const keys = this.getLocalStorageKeys();
    return keys[key] || `${this.getDomainConfig().storagePrefix}${key}`;
  }

  /**
   * 获取当前环境（beta 或 production）
   */
  public getCurrentEnvironment(): 'beta' | 'production' {
    try {
      const origin = window.location.origin;
      const config = this.getDomainConfig();

      // 检查是否是 beta 环境
      if (
        config.domains.beta.some(
          (domain) => origin.startsWith(domain) || origin.endsWith(domain.replace('https://', '')),
        )
      ) {
        return 'beta';
      }

      return 'production';
    } catch (error) {
      console.warn('Environment detection failed, falling back to production', error);
      return 'production';
    }
  }

  /**
   * 获取登录URL
   */
  public getLoginUrl(): string {
    const config = this.getDomainConfig();
    const environment = this.getCurrentEnvironment();
    return config.loginUrl[environment];
  }

  /**
   * 清除缓存（用于测试或需要重新检测时）
   */
  public clearCache(): void {
    this.cachedIsSinoMotive = null;
    this.cachedDomainConfig = null;
    this.cachedStorageKeys = null;
  }

  /**
   * 实际的域名检测逻辑
   */
  private detectSinoMotiveDomain(): boolean {
    try {
      const origin = window.location.origin;
      const sinoMotiveDomains = [
        ...DOMAIN_CONFIGS.SINOMOTIVE.domains.beta,
        ...DOMAIN_CONFIGS.SINOMOTIVE.domains.production,
      ];

      return sinoMotiveDomains.some(
        (domain) => origin.startsWith(domain) || origin.endsWith(domain.replace('https://', '')),
      );
    } catch (error) {
      console.warn('Domain detection failed, falling back to default', error);
      return false; // 默认为CassChoice
    }
  }
}

// 导出单例实例和便捷函数
const domainManager = DomainManager.getInstance();

// 保持向后兼容的导出函数
export const isSinoMotiveOrigin = () => domainManager.isSinoMotive();

// 导出新的域名管理器实例
export { domainManager as DomainManager };

// 导出便捷函数
export const getCurrentDomainConfig = () => domainManager.getDomainConfig();
export const getLocalStorageKeys = () => domainManager.getLocalStorageKeys();
export const getStorageKey = (key: LocalStorageKey) => domainManager.getStorageKey(key);
export const getLoginUrl = () => domainManager.getLoginUrl();
export const getCurrentEnvironment = () => domainManager.getCurrentEnvironment();

export const redirectToSinoMotiveLogin = () => {
  console.log('redirectToSinoMotiveLogin');
  localStorage.clear();
  if (['http://localhost:300'].some((domain) => window.location.origin.includes(domain))) {
    window.location.href = DOMAIN_CONFIGS.SINOMOTIVE.loginUrl.beta;
  } else if (
    DOMAIN_CONFIGS.CASSCHOICE.domains.beta.some(
      (domain) =>
        window.location.origin.startsWith(domain) ||
        window.location.origin.endsWith(domain.replace('https://', '')),
    )
  ) {
    window.location.href = DOMAIN_CONFIGS.SINOMOTIVE.loginUrl.beta;
  } else {
    window.location.href = DOMAIN_CONFIGS.SINOMOTIVE.loginUrl.production;
  }
};
